import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getRecurringScheduleByAgreement, saveRecurringSchedule } from '../utils/storageUtils';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './RosterView.css';

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'pm' : 'am';
  const h12 = hour % 12 || 12;
  return m === '00' ? `${h12}${ampm}` : `${h12}:${m}${ampm}`;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// Monday = 0, Sunday = 6
function getStartDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function getDayKey(date) {
  const jsDay = date.getDay(); // 0=Sun
  return DAY_KEYS[jsDay === 0 ? 6 : jsDay - 1];
}

function isToday(year, month, day) {
  const now = new Date();
  return now.getFullYear() === year && now.getMonth() === month && now.getDate() === day;
}

export default function RosterView({ agreement, client, onClose, onEditSchedule }) {
  const { currentUser } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [schedule, setSchedule] = useState(null);
  const [editingShift, setEditingShift] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [autoInvoiceOpen, setAutoInvoiceOpen] = useState(false);
  const [autoInvoice, setAutoInvoice] = useState({
    enabled: false,
    frequency: 'fortnightly',
    startDate: '',
    invoiceDay: 'monday',
    sendMethod: 'review-first',
    planManagerEmail: '',
    userPhone: '',
    userId: '',
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadSchedule = useCallback(() => {
    if (agreement?.id) {
      const sched = getRecurringScheduleByAgreement(agreement.id);
      setSchedule(sched);
      if (sched?.autoInvoice) {
        setAutoInvoice(prev => ({ ...prev, ...sched.autoInvoice, userId: currentUser?.uid || prev.userId }));
      } else {
        setAutoInvoice(prev => ({
          ...prev,
          planManagerEmail: client?.planManagerEmail || prev.planManagerEmail,
          userId: currentUser?.uid || prev.userId,
        }));
      }
    }
  }, [agreement, client]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  // Map day keys to their shifts
  const shiftsByDayKey = useMemo(() => {
    if (!schedule?.blocks) return {};
    const result = {};
    DAY_KEYS.forEach(dk => { result[dk] = []; });
    schedule.blocks.forEach((block, blockIndex) => {
      DAY_KEYS.forEach(dk => {
        const d = block.days?.[dk];
        if (d && d.duration) {
          result[dk].push({ ...d, blockIndex });
        }
      });
    });
    return result;
  }, [schedule]);

  // Calendar grid
  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);
    const startDay = getStartDayOfMonth(year, month);
    const cells = [];

    // Leading empty cells
    for (let i = 0; i < startDay; i++) {
      cells.push({ day: null, key: `empty-${i}` });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dayKey = getDayKey(date);
      cells.push({
        day: d,
        dayKey,
        date,
        shifts: shiftsByDayKey[dayKey] || [],
        today: isToday(year, month, d),
        key: `day-${d}`,
      });
    }

    return cells;
  }, [year, month, shiftsByDayKey]);

  const totalWeeklyHours = useMemo(() => {
    let total = 0;
    Object.values(shiftsByDayKey).forEach(shifts => {
      shifts.forEach(s => { total += (s.duration || 0); });
    });
    return total;
  }, [shiftsByDayKey]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const goToToday = () => {
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  };

  const openEditShift = (blockIndex, dayKey, shift, day) => {
    setEditingShift({ blockIndex, dayKey, day });
    setEditForm({
      startTime: shift.startTime || '',
      endTime: shift.endTime || '',
      duration: shift.duration || '',
    });
  };

  const saveShiftEdit = () => {
    if (!editingShift || !schedule) return;
    const { blockIndex, dayKey } = editingShift;
    const updated = { ...schedule };
    updated.blocks = updated.blocks.map((b, i) => {
      if (i !== blockIndex) return b;
      return {
        ...b,
        days: {
          ...b.days,
          [dayKey]: {
            ...b.days[dayKey],
            startTime: editForm.startTime || null,
            endTime: editForm.endTime || null,
            duration: editForm.duration ? parseFloat(editForm.duration) : null,
          }
        }
      };
    });
    updated.updatedAt = new Date().toISOString();
    saveRecurringSchedule(updated);
    setSchedule(updated);
    setEditingShift(null);
  };

  const deleteShift = () => {
    if (!editingShift || !schedule) return;
    const { blockIndex, dayKey } = editingShift;
    const updated = { ...schedule };
    updated.blocks = updated.blocks.map((b, i) => {
      if (i !== blockIndex) return b;
      return {
        ...b,
        days: {
          ...b.days,
          [dayKey]: { duration: null, startTime: null, endTime: null, serviceItemId: null, serviceItemName: null, serviceItemNumber: null, unit: null }
        }
      };
    });
    updated.updatedAt = new Date().toISOString();
    saveRecurringSchedule(updated);
    setSchedule(updated);
    setEditingShift(null);
  };

  const saveAutoInvoice = (updates) => {
    const updated = { ...autoInvoice, ...updates, userId: currentUser?.uid || autoInvoice.userId };
    setAutoInvoice(updated);
    if (schedule) {
      const updatedSchedule = { ...schedule, autoInvoice: updated, updatedAt: new Date().toISOString() };
      saveRecurringSchedule(updatedSchedule);
      setSchedule(updatedSchedule);
    }
  };

  const participantName = agreement?.participant?.name || 'Client';

  return (
    <div className="roster-overlay" onClick={onClose}>
      <div className="roster-modal" onClick={e => e.stopPropagation()}>
        <button className="btn-close" onClick={onClose} style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10 }}>×</button>

        {/* Header */}
        <div className="roster-header">
          <div>
            <h2 style={{ margin: 0 }}>{participantName}'s Roster</h2>
            {totalWeeklyHours > 0 && (
              <p className="roster-subtitle">{totalWeeklyHours}h per week</p>
            )}
          </div>
          <button className="btn btn-secondary btn-small" onClick={onEditSchedule}>
            ✏️ Edit Schedule
          </button>
        </div>

        {/* Month navigation */}
        <div className="roster-month-nav">
          <button className="roster-nav-btn" onClick={prevMonth} aria-label="Previous month">
            <ChevronLeft size={20} />
          </button>
          <div className="roster-month-label">
            <span className="roster-month-name">{MONTH_NAMES[month]}</span>
            <span className="roster-year">{year}</span>
          </div>
          <button className="roster-nav-btn" onClick={nextMonth} aria-label="Next month">
            <ChevronRight size={20} />
          </button>
          <button className="roster-today-btn" onClick={goToToday}>Today</button>
        </div>

        {/* Calendar */}
        <div className="roster-calendar-grid">
          {/* Day of week headers */}
          {DAYS_OF_WEEK.map(d => (
            <div key={d} className="roster-cal-header">{d}</div>
          ))}

          {/* Day cells */}
          {calendarDays.map(cell => (
            <div
              key={cell.key}
              className={`roster-cal-cell${cell.day ? '' : ' empty'}${cell.today ? ' today' : ''}`}
            >
              {cell.day && (
                <>
                  <span className={`roster-cal-date${cell.today ? ' today' : ''}`}>{cell.day}</span>
                  <div className="roster-cal-shifts">
                    {cell.shifts.map((shift, si) => (
                      <div
                        key={si}
                        className="roster-cal-shift"
                        onClick={() => openEditShift(shift.blockIndex, cell.dayKey, shift, cell.day)}
                        title="Click to edit"
                      >
                        {shift.startTime
                          ? (isMobile
                            ? formatTime(shift.startTime)
                            : `${formatTime(shift.startTime)} – ${formatTime(shift.endTime)}`)
                          : `${shift.duration}h`}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Auto Invoice Settings */}
        <div className="auto-invoice-section">
          <button
            className="auto-invoice-toggle"
            onClick={() => setAutoInvoiceOpen(!autoInvoiceOpen)}
          >
            <div className="auto-invoice-toggle-left">
              <Zap size={18} />
              <span>Auto Invoice</span>
              {autoInvoice.enabled && <span className="auto-invoice-active-badge">Active</span>}
            </div>
            {autoInvoiceOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          {autoInvoiceOpen && (
            <div className="auto-invoice-body">
              <div className="auto-invoice-enable-row">
                <label className="auto-invoice-label">Enable auto-invoicing</label>
                <button
                  className={`toggle-switch ${autoInvoice.enabled ? 'on' : ''}`}
                  onClick={() => saveAutoInvoice({ enabled: !autoInvoice.enabled })}
                  aria-label="Toggle auto-invoicing"
                >
                  <span className="toggle-knob" />
                </button>
              </div>

              {autoInvoice.enabled && (
                <>
                  <div className="auto-invoice-field">
                    <label className="auto-invoice-label">Invoice Frequency</label>
                    <select
                      value={autoInvoice.frequency}
                      onChange={e => saveAutoInvoice({ frequency: e.target.value })}
                      className="auto-invoice-input"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="fortnightly">Fortnightly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  <div className="auto-invoice-field">
                    <label className="auto-invoice-label">Start Date</label>
                    <input
                      type="date"
                      value={autoInvoice.startDate}
                      onChange={e => saveAutoInvoice({ startDate: e.target.value })}
                      className="auto-invoice-input"
                    />
                  </div>

                  <div className="auto-invoice-field">
                    <label className="auto-invoice-label">Invoice Day</label>
                    <select
                      value={autoInvoice.invoiceDay}
                      onChange={e => saveAutoInvoice({ invoiceDay: e.target.value })}
                      className="auto-invoice-input"
                    >
                      <option value="monday">Monday</option>
                      <option value="tuesday">Tuesday</option>
                      <option value="wednesday">Wednesday</option>
                      <option value="thursday">Thursday</option>
                      <option value="friday">Friday</option>
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                    </select>
                  </div>

                  <div className="auto-invoice-field">
                    <label className="auto-invoice-label">Plan Manager Email</label>
                    <input
                      type="email"
                      value={autoInvoice.planManagerEmail}
                      onChange={e => saveAutoInvoice({ planManagerEmail: e.target.value })}
                      placeholder="pm@example.com"
                      className="auto-invoice-input"
                    />
                  </div>

                  <div className="auto-invoice-field">
                    <label className="auto-invoice-label">Your Phone Number (for SMS approvals)</label>
                    <input
                      type="tel"
                      value={autoInvoice.userPhone}
                      onChange={e => saveAutoInvoice({ userPhone: e.target.value })}
                      placeholder="+61400000000"
                      className="auto-invoice-input"
                    />
                  </div>

                  <p className="auto-invoice-hint">
                    We'll generate the invoice and SMS you a summary to review. Reply YES to approve and send to the plan manager. This ensures you've verified every invoice before it goes out.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Edit shift popup */}
        {editingShift && (
          <div className="roster-edit-overlay" onClick={() => setEditingShift(null)}>
            <div className="roster-edit-popup" onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 1rem' }}>
                Edit Shift — {DAYS_OF_WEEK[DAY_KEYS.indexOf(editingShift.dayKey)]}, {MONTH_NAMES[month]} {editingShift.day}
              </h3>

              <label className="roster-edit-label">Start Time</label>
              <input
                type="time"
                value={editForm.startTime}
                onChange={e => setEditForm(f => ({ ...f, startTime: e.target.value }))}
                className="roster-edit-input"
              />

              <label className="roster-edit-label">End Time</label>
              <input
                type="time"
                value={editForm.endTime}
                onChange={e => setEditForm(f => ({ ...f, endTime: e.target.value }))}
                className="roster-edit-input"
              />

              <label className="roster-edit-label">Duration (hours)</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={editForm.duration}
                onChange={e => setEditForm(f => ({ ...f, duration: e.target.value }))}
                className="roster-edit-input"
              />

              <div className="roster-edit-actions">
                <button className="btn btn-primary" onClick={saveShiftEdit}>Save</button>
                <button className="btn btn-secondary" onClick={() => setEditingShift(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={deleteShift} style={{ marginLeft: 'auto' }}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
